import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FREQ_ICON = {
  once:         'bi-1-circle-fill',
  monthly:      'bi-arrow-repeat',
  twice_yearly: 'bi-calendar2-range-fill',
  yearly:       'bi-calendar-check-fill',
};

function formatYen(n) {
  return `¥${Number(n || 0).toLocaleString('ja-JP')}`;
}

function StatusBadge({ status }) {
  const map = {
    completed: { cls: 'badge-completed', icon: 'bi-check-circle-fill' },
    pending:   { cls: 'badge-pending',   icon: 'bi-clock-fill' },
    failed:    { cls: 'badge-failed',    icon: 'bi-x-circle-fill' },
    cancelled: { cls: 'badge-cancelled', icon: 'bi-dash-circle-fill' },
  };
  const b = map[status] || map.pending;
  return (
    <span className={`badge rounded-pill px-3 py-2 ${b.cls}`}>
      <i className={`bi ${b.icon} me-1`} />{status}
    </span>
  );
}

export default function DonorProfile() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  useEffect(() => {
    axios.get(`/api/donor/${token}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [token]);

  const locale = i18n.language || 'ja';
  const isJa   = locale === 'ja';

  if (loading) return (
    <>
      <Navbar />
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} />
      </div>
      <Footer />
    </>
  );

  if (error || !data) return (
    <>
      <Navbar />
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center p-5">
          <div className="icon-circle icon-circle-danger mx-auto mb-4" style={{ width: 80, height: 80, fontSize: '2rem' }}>
            <i className="bi bi-exclamation-triangle-fill" />
          </div>
          <h4>{isJa ? 'ページが見つかりません' : 'Page Not Found'}</h4>
          <p className="text-muted">{isJa ? '正しいリンクをご確認ください。' : 'Please check your link.'}</p>
          <Link to="/" className="btn btn-donate px-4 py-2 mt-2">
            <i className="bi bi-house-fill me-2" />{isJa ? 'ホームへ' : 'Go Home'}
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );

  const { donor, donations, total_amount } = data;

  async function openReceipt(donationId) {
    try {
      const res = await fetch(`/api/donor/${token}/receipt/${donationId}?locale=${locale}`);
      const html = await res.text();
      const blob = new Blob([html], { type: 'text/html' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err) {
      alert('Failed to open receipt: ' + err.message);
    }
  }

  return (
    <>
      <Navbar />

      {/* Hero banner */}
      <div className="bg-festival text-white py-5">
        <div className="container">
          <div className="d-flex align-items-center gap-4">
            <div style={{
              width: 80, height: 80, borderRadius: '50%', flexShrink: 0,
              background: 'rgba(255,255,255,.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid rgba(255,255,255,.3)',
            }}>
              <i className="bi bi-person-fill" style={{ fontSize: '2.2rem', color: 'rgba(255,255,255,.9)' }} />
            </div>
            <div>
              <p className="mb-1 small" style={{ color: 'rgba(255,255,255,.6)', letterSpacing: '.08em' }}>
                <i className="bi bi-shield-lock-fill me-2" style={{ color: '#ffc107' }} />
                {isJa ? '寄付者マイページ' : 'Donor Profile'}
              </p>
              <h2 className="fw-bold mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>{donor.name}</h2>
              <p className="mb-0 small" style={{ color: 'rgba(255,255,255,.6)' }}>
                <i className="bi bi-envelope-fill me-2" />{donor.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="py-5" style={{ background: '#f8f9fa', minHeight: '60vh' }}>
        <div className="container">

          {/* Summary cards */}
          <div className="row g-3 mb-4">
            {[
              {
                icon: 'bi-heart-fill',
                color: '#d63384',
                label: isJa ? '総寄付件数' : 'Total Donations',
                value: donations.length,
              },
              {
                icon: 'bi-currency-yen',
                color: '#fd7e14',
                label: isJa ? '累計寄付金額' : 'Total Amount',
                value: formatYen(total_amount),
              },
              {
                icon: 'bi-calendar3',
                color: '#198754',
                label: isJa ? '最初の寄付' : 'First Donation',
                value: donations.length
                  ? new Date(donations[donations.length - 1].created_at)
                      .toLocaleDateString(isJa ? 'ja-JP' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                  : '—',
              },
            ].map((s, i) => (
              <div key={i} className="col-md-4">
                <div className="card border-0 shadow-sm rounded-4 p-4 h-100 card-lift">
                  <div className="d-flex align-items-center gap-3">
                    <div style={{
                      width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                      background: `${s.color}12`,
                      border: `1.5px solid ${s.color}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: '1.4rem' }} />
                    </div>
                    <div>
                      <div className="text-muted small">{s.label}</div>
                      <div className="fw-bold fs-5" style={{ color: s.color }}>{s.value}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Donations table */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="px-4 py-3 border-bottom d-flex align-items-center gap-2"
              style={{ background: '#fff' }}>
              <i className="bi bi-receipt fs-5" style={{ color: 'var(--primary)' }} />
              <h6 className="fw-bold mb-0">{isJa ? '寄付履歴' : 'Donation History'}</h6>
            </div>

            {donations.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox fs-2 d-block mb-2" />
                {isJa ? '寄付履歴はありません' : 'No donations yet'}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0 admin-table">
                  <thead>
                    <tr>
                      <th className="ps-4">{isJa ? '日付' : 'Date'}</th>
                      <th>{isJa ? '金額' : 'Amount'}</th>
                      <th>{isJa ? '頻度' : 'Frequency'}</th>
                      <th>{isJa ? '年間合計' : 'Yearly Total'}</th>
                      <th>{isJa ? '領収書番号' : 'Receipt No.'}</th>
                      <th>{isJa ? '領収書' : 'Receipt'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map(d => (
                      <tr key={d.id}>
                        <td className="ps-4 small text-muted">
                          {new Date(d.created_at).toLocaleDateString(isJa ? 'ja-JP' : 'en-US', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                        </td>
                        <td className="fw-bold" style={{ color: 'var(--primary)' }}>
                          {formatYen(d.amount)}
                        </td>
                        <td>
                          <span className="d-flex align-items-center gap-1 small">
                            <i className={`bi ${FREQ_ICON[d.frequency]}`} style={{ color: 'var(--secondary)' }} />
                            {isJa
                              ? { once: '一回限り', monthly: '毎月', twice_yearly: '年2回', yearly: '毎年' }[d.frequency]
                              : { once: 'One-time', monthly: 'Monthly', twice_yearly: 'Twice/year', yearly: 'Yearly' }[d.frequency]
                            }
                          </span>
                        </td>
                        <td className="small">{formatYen(d.yearly_total)}</td>
                        <td>
                          {d.receipt_number
                            ? <span className="badge rounded-pill bg-light text-dark border small">{d.receipt_number}</span>
                            : <span className="text-muted small">—</span>
                          }
                        </td>
                        <td>
                          {d.receipt_number ? (
                            <button
                              className="btn btn-sm btn-outline-primary rounded-pill px-3"
                              onClick={() => openReceipt(d.id)}
                            >
                              <i className="bi bi-download me-1" />
                              {isJa ? '表示' : 'View'}
                            </button>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Privacy note */}
          <div className="mt-4 p-3 rounded-3 d-flex align-items-start gap-3"
            style={{ background: 'rgba(255,193,7,.08)', border: '1px solid rgba(255,193,7,.3)' }}>
            <i className="bi bi-shield-lock-fill mt-1" style={{ color: '#856404', flexShrink: 0 }} />
            <p className="mb-0 small" style={{ color: '#856404' }}>
              {isJa
                ? 'このページのURLはあなただけの専用リンクです。第三者と共有しないようにご注意ください。'
                : 'This page URL is your private link. Please do not share it with others.'
              }
            </p>
          </div>

        </div>
      </div>

      <Footer />
    </>
  );
}
